import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  dictionaryApi,
  ChannelManagerReconcileStatus,
} from "../../../api/metadataSpellcheckApi";
import "./SpellcheckAdminTab.css";

const SpellcheckAdminTab = () => {
  const toast = useRef<Toast>(null);
  const currentUser = useSelector((state: any) => state.googleAuth.user);
  const isAdmin = currentUser?.roles?.role === "admin";

  const [status, setStatus] = useState<ChannelManagerReconcileStatus | null>(
    null
  );
  const [channelId, setChannelId] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingStatus(true);
    try {
      const res = await dictionaryApi.getChannelManagerReconcileStatus();
      setStatus(res.status);
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        summary: "Could not load last run",
        detail: e?.response?.data?.error || e?.message || "Request failed",
        life: 4000,
      });
    } finally {
      setLoadingStatus(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const runReconcile = (asDryRun: boolean) => {
    confirmDialog({
      header: asDryRun ? "Dry-run channel manager reconcile" : "Update issue managers",
      message: asDryRun
        ? "Scan issues against live channelSettings and report how many would change. No documents are written."
        : "Copy current channelSettings manager emails onto Title QC issues. Your Issues membership will follow the new managers. Continue?",
      acceptLabel: asDryRun ? "Dry run" : "Update now",
      rejectLabel: "Cancel",
      acceptClassName: asDryRun ? "p-button-secondary" : "p-button-danger",
      accept: () => {
        void (async () => {
          setRunning(true);
          try {
            const res = await dictionaryApi.runChannelManagerReconcile({
              dryRun: asDryRun,
              channelId: channelId.trim() || undefined,
            });
            setStatus(res.status);
            toast.current?.show({
              severity: "success",
              summary: asDryRun ? "Dry run finished" : "Reconcile finished",
              detail: `Scanned ${res.status.scanned}, would/did update ${res.status.updated}.`,
              life: 6000,
            });
          } catch (e: any) {
            toast.current?.show({
              severity: "error",
              summary: "Reconcile failed",
              detail: e?.response?.data?.error || e?.message || "Request failed",
              life: 6000,
            });
          } finally {
            setRunning(false);
          }
        })();
      },
    });
  };

  if (!isAdmin) {
    return (
      <div className="spellcheck-admin-tab">
        <p className="spellcheck-admin-muted">
          This tab is limited to admin users.
        </p>
      </div>
    );
  }

  return (
    <div className="spellcheck-admin-tab">
      <Toast ref={toast} />
      <ConfirmDialog />
      <Card className="spellcheck-admin-card">
        <h3 className="spellcheck-admin-title">Channel manager reconcile</h3>
        <p className="spellcheck-admin-desc">
          Title QC snapshots VOD/Shorts manager emails onto each issue when the
          spellcheck worker writes it. If a channel manager later changes in{" "}
          <code>channelSettings</code>, Your Issues stays on the old emails until
          this job copies the live assignment onto existing issues. A Cloud
          Scheduler job also runs this Mon/Wed/Fri at 06:00 UTC; use this tab for
          dry runs or an immediate update.
        </p>
        <div className="spellcheck-admin-form">
          <div>
            <label className="spellcheck-admin-field-label" htmlFor="qc-admin-channel">
              Channel ID (optional)
            </label>
            <InputText
              id="qc-admin-channel"
              className="spellcheck-admin-channel-input"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder="Leave blank for all channels"
              disabled={running}
            />
          </div>
          <div className="spellcheck-admin-dry-run">
            <InputSwitch
              checked={dryRun}
              onChange={(e) => setDryRun(Boolean(e.value))}
              disabled={running}
            />
            <span className="spellcheck-admin-dry-run-label">
              Dry run (count changes, do not write)
            </span>
          </div>
          <div className="spellcheck-admin-actions">
            <Button
              label={dryRun ? "Run dry run" : "Update managers"}
              icon="pi pi-refresh"
              onClick={() => runReconcile(dryRun)}
              loading={running}
              disabled={running}
            />
            <Button
              label="Refresh last run"
              icon="pi pi-clock"
              className="p-button-outlined"
              onClick={() => void loadStatus()}
              loading={loadingStatus}
              disabled={running}
            />
          </div>
        </div>
        {status ? (
          <div className="spellcheck-admin-status">
            <p className="spellcheck-admin-status-title">
              Last run: {status.status}
              {status.dryRun ? " (dry run)" : ""}
            </p>
            <div className="spellcheck-admin-status-grid">
              <span>Triggered by</span>
              <span>{status.triggeredBy}</span>
              <span>Started</span>
              <span>{status.startedAt}</span>
              <span>Finished</span>
              <span>{status.completedAt || "—"}</span>
              <span>Channel filter</span>
              <span>{status.channelId || "all"}</span>
              <span>Channels in settings</span>
              <span>{status.channelsInSettings}</span>
              <span>Issues scanned</span>
              <span>{status.scanned}</span>
              <span>Updated</span>
              <span>{status.updated}</span>
              <span>Already current</span>
              <span>{status.unchanged}</span>
              <span>No channel id</span>
              <span>{status.missingChannelId}</span>
              <span>No channelSettings row</span>
              <span>{status.missingChannel}</span>
              <span>Empty managers on channel</span>
              <span>{status.skippedEmptyManagers}</span>
              <span>Duration</span>
              <span>
                {status.durationMs != null
                  ? `${(status.durationMs / 1000).toFixed(1)}s`
                  : "—"}
              </span>
            </div>
            {status.error ? (
              <p className="spellcheck-admin-status-error">{status.error}</p>
            ) : null}
          </div>
        ) : (
          <p className="spellcheck-admin-muted">No reconcile has been run yet.</p>
        )}
      </Card>
    </div>
  );
};

export default SpellcheckAdminTab;
